import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitCompareArrows, Plus, X, Eye, Wind, Wine as WineIcon, FileText, Info, Radar as RadarIcon } from "lucide-react";
import { categoriaPuntuacion, AROMAS, INTENSIDAD_5, NIVEL_5, CUERPO, CANTIDAD_3, DULZOR } from "@/lib/tastingOptions";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";

const RADAR_COLORS = ["#722F37", "#D4AF37", "#374151"];
const RADAR_TOOLTIP_STYLE = { background: "#0A0A0A", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 };
const RADAR_LEGEND_STYLE = { fontSize: 12 };
const ANGLE_TICK = { fontSize: 12, fill: "#4B5563" };
const RADIUS_TICK = { fontSize: 10, fill: "#9CA3AF" };
const mkSlot = () => ({ sid: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())), id: "" });

const scaleLevel = (list, value) => {
  const idx = list.indexOf(value);
  if (idx < 0) return 0;
  return Math.round(((idx / (list.length - 1)) * 4 + 1) * 10) / 10; // 1..5
};

const RADAR_AXES = [
  ["Intensidad", (t) => scaleLevel(INTENSIDAD_5, t.olfativo?.intensidad)],
  ["Acidez", (t) => scaleLevel(INTENSIDAD_5, t.gustativo?.acidez)],
  ["Alcohol", (t) => scaleLevel(NIVEL_5, t.gustativo?.alcohol)],
  ["Cuerpo", (t) => scaleLevel(CUERPO, t.gustativo?.cuerpo)],
  ["Taninos", (t) => scaleLevel(CANTIDAD_3, t.gustativo?.taninos_cantidad)],
  ["Dulzor", (t) => scaleLevel(DULZOR, t.gustativo?.dulzor)],
];

const fmt = (v) => {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
};

const SECTIONS = [
  {
    title: "General", icon: Info,
    rows: [
      ["Bodega", (t) => t.wine?.bodega],
      ["País", (t) => t.wine?.pais],
      ["Región", (t) => t.wine?.region],
      ["Variedad", (t) => t.wine?.variedad],
      ["Añada", (t) => t.wine?.anada],
      ["Tipo", (t) => t.wine?.tipo],
      ["Alcohol", (t) => t.wine?.alcohol && `${t.wine.alcohol}%`],
      ["Precio", (t) => t.wine?.precio],
    ],
  },
  {
    title: "Examen Visual", icon: Eye,
    rows: [
      ["Limpidez", (t) => t.visual?.limpidez],
      ["Brillantez", (t) => t.visual?.brillantez],
      ["Color", (t) => t.visual?.color],
      ["Intensidad", (t) => t.visual?.intensidad],
      ["Densidad", (t) => t.visual?.densidad],
    ],
  },
  {
    title: "Examen Olfativo", icon: Wind,
    rows: [
      ["Estado", (t) => t.olfativo?.estado],
      ["Primera impresión", (t) => t.olfativo?.primera_impresion],
      ["Intensidad", (t) => t.olfativo?.intensidad],
      ["Carácter", (t) => t.olfativo?.caracter],
      ["Barrica", (t) => t.olfativo?.barrica],
      ["Aromas", (t) => Object.keys(AROMAS).flatMap((f) => t.olfativo?.[`aromas_${f}`] || [])],
    ],
  },
  {
    title: "Examen Gustativo", icon: WineIcon,
    rows: [
      ["Dulzor", (t) => t.gustativo?.dulzor],
      ["Equilibrio", (t) => t.gustativo?.equilibrio],
      ["Acidez", (t) => t.gustativo?.acidez],
      ["Alcohol", (t) => t.gustativo?.alcohol],
      ["Taninos", (t) => [t.gustativo?.taninos_cantidad, t.gustativo?.taninos_calidad].filter(Boolean).join(" / ")],
      ["Cuerpo", (t) => t.gustativo?.cuerpo],
      ["Persistencia", (t) => t.gustativo?.persistencia],
      ["Estilo", (t) => t.gustativo?.estilo],
      ["Maridaje", (t) => t.gustativo?.maridaje],
    ],
  },
  {
    title: "Observaciones", icon: FileText,
    rows: [
      ["¿Volvería a comprar?", (t) => t.observaciones?.volveria],
      ["Calidad-precio", (t) => t.observaciones?.relacion_precio],
      ["Etiquetas", (t) => t.observaciones?.etiquetas],
    ],
  },
];

export default function Compare() {
  const navigate = useNavigate();
  const [slots, setSlots] = useState(() => [mkSlot(), mkSlot()]);

  const { data: tastings = [], isLoading } = useQuery({
    queryKey: ["tastings", {}],
    queryFn: async () => (await api.get("/tastings")).data,
  });

  const byId = useMemo(() => Object.fromEntries(tastings.map((t) => [t.id, t])), [tastings]);
  const selectedIds = useMemo(() => slots.map((s) => s.id), [slots]);
  const chosen = useMemo(() => slots.map((s) => byId[s.id]).filter(Boolean), [slots, byId]);
  const maxScore = useMemo(() => (chosen.length ? Math.max(...chosen.map((t) => t.puntuacion || 0)) : 0), [chosen]);
  const gridTemplate = useMemo(() => ({ gridTemplateColumns: `160px repeat(${chosen.length}, minmax(0,1fr))` }), [chosen.length]);
  const selectorGrid = useMemo(() => ({ gridTemplateColumns: `repeat(${slots.length}, minmax(0,1fr))` }), [slots.length]);
  const radarData = useMemo(
    () => RADAR_AXES.map(([label, get]) => {
      const row = { attribute: label };
      chosen.forEach((t, i) => { row[`c${i}`] = get(t); });
      return row;
    }),
    [chosen]
  );
  const hasRadarData = useMemo(() => chosen.some((t) => RADAR_AXES.some(([, get]) => get(t) > 0)), [chosen]);

  const setSlot = (i) => (v) => setSlots((s) => s.map((x, idx) => (idx === i ? { ...x, id: v } : x)));
  const addSlot = () => setSlots((s) => (s.length < 3 ? [...s, mkSlot()] : s));
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-1">Análisis</p>
        <h1 className="font-serif text-4xl text-negro">Comparar catas</h1>
        <p className="text-grises-500 mt-1">Selecciona hasta 3 catas para verlas lado a lado.</p>
      </div>

      {isLoading ? (
        <p className="text-grises-400">Cargando...</p>
      ) : tastings.length < 2 ? (
        <Card className="p-12 text-center border-grises-200 rounded-xl">
          <GitCompareArrows className="w-12 h-12 text-borgona-300 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-grises-500">Necesitas al menos 2 catas para comparar.</p>
        </Card>
      ) : (
        <>
          {/* Selectors */}
          <div className="grid gap-4 mb-6" style={selectorGrid}>
            {slots.map((slot, i) => (
              <div key={slot.sid} className="relative">
                <Select value={slot.id} onValueChange={setSlot(i)}>
                  <SelectTrigger data-testid={`compare-select-${i}`} className="h-11">
                    <SelectValue placeholder={`Cata ${i + 1}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {tastings.map((t) => (
                      <SelectItem key={t.id} value={t.id} disabled={selectedIds.includes(t.id) && slot.id !== t.id}>
                        {t.wine?.nombre} · {t.puntuacion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {slots.length > 2 && (
                  <button data-testid={`remove-slot-${i}`} onClick={() => removeSlot(i)}
                    className="absolute -top-2 -right-2 bg-grises-200 hover:bg-destructive hover:text-blanco rounded-full p-1 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {slots.length < 3 && (
            <Button data-testid="add-compare-slot" variant="outline" onClick={addSlot}
              className="rounded-full border-grises-300 mb-8">
              <Plus className="w-4 h-4 mr-1" /> Añadir cata
            </Button>
          )}

          {chosen.length >= 2 && (
            <Card className="border-grises-200 rounded-xl overflow-hidden">
              {/* Score header */}
              <div className="grid border-b border-grises-200 bg-grises-50" style={gridTemplate}>
                <div className="p-4" />
                {chosen.map((t) => {
                  const cat = categoriaPuntuacion(t.puntuacion);
                  const isBest = t.puntuacion === maxScore;
                  return (
                    <div key={t.id} className="p-4 text-center border-l border-grises-200 cursor-pointer hover:bg-blanco transition-colors"
                      onClick={() => navigate(`/app/catas/${t.id}`)} data-testid={`compare-col-${t.id}`}>
                      {t.wine?.foto_botella && (
                        <img src={fileUrl(t.wine.foto_botella)} alt="" className="w-14 h-20 object-cover rounded-lg mx-auto mb-2 border border-grises-200" />
                      )}
                      <div className="font-serif text-negro leading-tight text-sm">{t.wine?.nombre}</div>
                      <div className="text-xs text-grises-500 mb-2">{t.wine?.anada}</div>
                      <div className="font-mono text-3xl font-bold" style={{ color: cat.color }}>{t.puntuacion}</div>
                      <div className="text-[10px] uppercase tracking-wider" style={{ color: cat.color }}>{cat.label}</div>
                      {isBest && chosen.length > 1 && (
                        <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-dorado-500 text-negro font-medium">★ Mejor</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Radar - sensory profile */}
              {hasRadarData && (
                <div className="border-b border-grises-200 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <RadarIcon className="w-4 h-4 text-borgona-700" strokeWidth={1.75} />
                    <span className="font-serif text-borgona-900">Perfil sensorial</span>
                  </div>
                  <ResponsiveContainer width="100%" height={340}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#E5E7EB" />
                      <PolarAngleAxis dataKey="attribute" tick={ANGLE_TICK} />
                      <PolarRadiusAxis domain={[0, 5]} tick={RADIUS_TICK} tickCount={6} />
                      {chosen.map((t, i) => (
                        <Radar key={t.id} name={`${t.wine?.nombre || "Cata"} (${t.puntuacion})`} dataKey={`c${i}`}
                          stroke={RADAR_COLORS[i % RADAR_COLORS.length]} fill={RADAR_COLORS[i % RADAR_COLORS.length]} fillOpacity={0.18} strokeWidth={2} />
                      ))}
                      <Legend wrapperStyle={RADAR_LEGEND_STYLE} />
                      <Tooltip contentStyle={RADAR_TOOLTIP_STYLE} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Sections */}
              {SECTIONS.map((sec) => (
                <div key={sec.title}>
                  <div className="grid bg-borgona-50/60" style={gridTemplate}>
                    <div className="px-4 py-2.5 flex items-center gap-2 col-span-full">
                      <sec.icon className="w-4 h-4 text-borgona-700" strokeWidth={1.75} />
                      <span className="font-serif text-borgona-900">{sec.title}</span>
                    </div>
                  </div>
                  {sec.rows.map(([label, get], ri) => (
                    <div key={label} className={`grid ${ri % 2 ? "bg-grises-50/40" : ""}`} style={gridTemplate}>
                      <div className="px-4 py-2.5 text-sm text-grises-500">{label}</div>
                      {chosen.map((t) => (
                        <div key={t.id} className="px-4 py-2.5 text-sm text-negro border-l border-grises-100">{fmt(get(t))}</div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
